import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { TeamContext } from 'contexts/TeamContext';
import { createAPIClient } from 'api/client';
import AppContext from 'AppContext';

const BudgetStateContext = createContext();

const initialBudgetState = {
  categories: []
};

export const BudgetStateProvider = ({ children }) => {
  const [budgetState, setBudgetState] = useState(() => initialBudgetState);
  const [isLoading, setIsLoading] = useState(true);
  const [api, setApi] = useState(null);
  const { currentTeamId, loading: teamLoading } = useContext(TeamContext);
  const { platformUser, isLoading: isPlatformLoading } = useContext(AppContext);

  // Initialize API client
  useEffect(() => {
    const initAPI = async () => {
      try {
        const apiClient = await createAPIClient();
        setApi(apiClient);
      } catch (error) {
        console.error('Failed to initialize API client:', error);
      }
    };
    
    initAPI();
  }, []);

  // Load initial budget data
  useEffect(() => {
    const loadBudget = async () => {
      // Don't do anything while team is loading
      if (teamLoading) {
        return;
      }
      
      if (!currentTeamId || !api) {
        setIsLoading(false);
        setBudgetState(initialBudgetState);
        return;
      }
      
      try {
        setIsLoading(true);
        const response = await api.budgets.getBudget(currentTeamId);
        // Get the first budget or use initial state
        const firstBudget = response.data?.[0];
        setBudgetState(firstBudget?.data || initialBudgetState);
      } catch (error) {
        console.error('Error loading budget:', error);
        setBudgetState(initialBudgetState);
      } finally {
        setIsLoading(false);
      }
    };

    loadBudget();
  }, [currentTeamId, api, teamLoading]);

  const updateBudgetState = useCallback(async (updater) => {
    if (!currentTeamId || !api) {
      console.warn('Cannot update budget: Required data missing', {
        hasTeamId: !!currentTeamId,
        hasApi: !!api
      });
      return;
    }

    let newState;
    if (typeof updater === 'function') {
      newState = updater(budgetState);
    } else {
      newState = updater;
    }

    // Optimistically update local state
    setBudgetState(newState);

    try {
      // Update in database
      await api.budgets.updateBudget(currentTeamId, newState);
    } catch (error) {
      console.error('Error updating budget:', error);
      // Revert on error
      setBudgetState(budgetState);
    }
  }, [budgetState, currentTeamId, api]);

  const addRevenueCategory = useCallback(async () => {
    // console.log('Add category debug:', {
    //   hasTeamId: !!currentTeamId,
    //   teamId: currentTeamId,
    //   hasApi: !!api,
    //   api
    // });

    if (!currentTeamId || !api) {
      // console.warn('Cannot add category: Required data missing', {
      //   hasTeamId: !!currentTeamId,
      //   hasApi: !!api,
      //   teamId: currentTeamId,
      //   apiState: api
      // });
      return;
    }

    const newCategory = {
      name: `Revenue Category ${budgetState.categories.length + 1}`,
      products: []
    };

    const newState = {
      ...budgetState,
      categories: [...budgetState.categories, newCategory]
    };

    // Optimistically update local state
    setBudgetState(newState);

    try {
      // Update in database
      await api.budgets.updateBudget(currentTeamId, newState);
    } catch (error) {
      console.error('Error adding category:', error);
      // Revert on error
      setBudgetState(budgetState);
    }
  }, [budgetState, currentTeamId, api]);

  return (
    <BudgetStateContext.Provider 
      value={{ 
        budgetState, 
        updateBudgetState, 
        addRevenueCategory, 
        isLoading
      }}
    >
      {children}
    </BudgetStateContext.Provider>
  );
};

export const useBudgetState = () => {
  const context = useContext(BudgetStateContext);
  if (!context) {
    throw new Error('useBudgetState must be used within a BudgetStateProvider');
  }
  return context;
}; 